import LikeIcon from '../../../images/icons/like.svg?react';
import ThumbsDownIcon from '../../../images/icons/thumbs-down.svg?react';
import ThumbsUpIcon from '../../../images/icons/thumbs-up.svg?react';
import {Comment, useAppContext} from '../../../app-context';
import {LikeAvatar} from '../like-avatar';
import {useCommentSettings} from '../../../comment-settings-context';
import {useState} from 'react';

type Props = {
    comment: Comment;
    disabled?: boolean;
    setDisabled?: (disabled: boolean) => void;
};
const LikeButton: React.FC<Props> = ({comment, disabled, setDisabled}) => {
    const {dispatchAction, isMember, hasRequiredTier, capabilities, member, t} = useAppContext();
    // [unleash] コメントいいねリスト機能 + 色設定
    const {settings} = useCommentSettings();
    const inactiveColor = settings.text_color || '#333333';
    const [likeAnimation, setLikeAnimation] = useState('');
    const [localDisabled, setLocalDisabled] = useState(false);
    const [localComment, setLocalComment] = useState<Comment>({
        ...comment,
        likes_member: comment.likes_member || []
    });
    const [showLikes, setShowLikes] = useState(false);

    const canInteract = isMember && hasRequiredTier;
    const likesCount = localComment.count.likes;
    const dislikesEnabled = capabilities?.dislikes === true;
    const buttonDisabled = disabled ?? localDisabled;
    const setButtonDisabled = setDisabled ?? setLocalDisabled;

    const openCta = () => {
        dispatchAction('openPopup', {
            type: 'ctaPopup'
        });
    };

    const toggleLike = async () => {
        if (!canInteract) {
            openCta();
            return;
        }

        if (!member) {
            return;
        }

        setButtonDisabled(true);
        const prevComment = localComment;
        try {
            if (!localComment.liked) {
                // [unleash] 楽観的更新: いいねした人リストへ自分を追加
                setLocalComment((prev) => {
                    const updated = {...prev};

                    updated.liked = true;
                    updated.count = {...prev.count, likes: prev.count.likes + 1};

                    const prevLikesMember = Array.isArray(prev.likes_member) ? prev.likes_member : [];
                    const alreadyExists = prevLikesMember.some(m => m.uuid === member.uuid);
                    updated.likes_member = alreadyExists ? prevLikesMember : [...prevLikesMember, member];

                    return updated;
                });

                await dispatchAction('likeComment', localComment);
                setLikeAnimation('animate-heartbeat');
                setTimeout(() => {
                    setLikeAnimation('');
                }, 400);
            } else {
                // [unleash] 楽観的更新: いいねした人リストから自分を除去
                setLocalComment((prev) => {
                    const updated = {...prev};

                    updated.liked = false;
                    updated.count = {...prev.count, likes: Math.max(prev.count.likes - 1, 0)};

                    const prevLikesMember = Array.isArray(prev.likes_member) ? prev.likes_member : [];
                    updated.likes_member = prevLikesMember.filter(m => m.uuid !== member.uuid);

                    return updated;
                });

                await dispatchAction('unlikeComment', localComment);
            }
        } catch {
            setLocalComment(prevComment);
        } finally {
            setButtonDisabled(false);
        }
    };

    const likeButton = !dislikesEnabled ? (
        <button
            aria-label={localComment.liked ? t('Remove like') : t('Like')}
            className={`duration-50 group flex cursor-pointer items-center font-sans text-base outline-0 transition-all ease-linear sm:text-sm ${
                localComment.liked ? 'text-black/90 dark:text-white/90' : 'text-black/50 hover:text-black/75 dark:text-white/60 dark:hover:text-white/75'
            }`}
            data-testid="like-button"
            disabled={buttonDisabled}
            style={!localComment.liked ? {color: inactiveColor} : undefined}
            type="button"
            onClick={toggleLike}
        >
            <LikeIcon
                className={likeAnimation + ` mr-[6px] ${
                    localComment.liked ? 'fill-red-500 stroke-red-500' : 'stroke-black/50 group-hover:stroke-black/75 dark:stroke-white/60 dark:group-hover:stroke-white/75'
                } ${!localComment.liked && canInteract && 'group-hover:stroke-black/75 dark:group-hover:stroke-white/75'} transition duration-50 ease-linear`}
                style={!localComment.liked ? {stroke: inactiveColor} : undefined}
            />
            {likesCount}
        </button>
    ) : (
        <button
            aria-label={localComment.liked ? t('Remove like') : t('Like')}
            className={`duration-50 group flex cursor-pointer items-center gap-1.5 font-sans text-base outline-0 transition-all ease-linear sm:text-sm ${
                localComment.liked ? 'text-black/90 dark:text-white/90' : 'text-black/50 hover:text-black/75 dark:text-white/60 dark:hover:text-white/75'
            }`}
            data-testid="like-button"
            disabled={buttonDisabled}
            style={!localComment.liked ? {color: inactiveColor} : undefined}
            type="button"
            onClick={toggleLike}
        >
            <ThumbsUpIcon
                className={likeAnimation + ` h-4 w-4 ${
                    localComment.liked ? 'fill-black dark:fill-white stroke-black dark:stroke-white' : 'stroke-black/50 group-hover:stroke-black/75 dark:stroke-white/60 dark:group-hover:stroke-white/75'
                } ${!localComment.liked && canInteract && 'group-hover:stroke-black/75 dark:group-hover:stroke-white/75'} transition duration-50 ease-linear`}
            />
            <span className="tabular-nums">
                {likesCount}
            </span>
        </button>
    );

    return (
        <div
            className="relative inline-block"
            onMouseEnter={() => setShowLikes(true)}
            onMouseLeave={() => setShowLikes(false)}
            onTouchEnd={() => setShowLikes(false)}
            onTouchStart={() => setShowLikes(true)}
        >
            {likeButton}

            {/* [unleash] いいねした人リスト */}
            {showLikes && localComment.likes_member && localComment.likes_member.length > 0 && (
                <div
                    className="absolute bottom-6 left-0 z-10 mt-2 max-h-64 w-52 overflow-y-auto rounded border border-gray-200 bg-white px-3 py-2 text-black shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                    {localComment.likes_member.map(m => (
                        <div key={m.id} className="mb-1 flex items-center justify-between gap-2 last:mb-0">
                            <div className="flex items-center gap-2">
                                <LikeAvatar member={m} />
                                <span className="text-[10px]">{m.name}</span>
                            </div>
                            <LikeIcon className="ml-auto fill-red-500 stroke-red-500" />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const DislikeButton: React.FC<Props> = ({comment, disabled, setDisabled}) => {
    const {dispatchAction, isMember, hasRequiredTier, t} = useAppContext();
    const [dislikeAnimation, setDislikeAnimation] = useState('');
    const [localDisabled, setLocalDisabled] = useState(false);

    const canInteract = isMember && hasRequiredTier;
    const buttonDisabled = disabled ?? localDisabled;
    const setButtonDisabled = setDisabled ?? setLocalDisabled;

    const openCta = () => {
        dispatchAction('openPopup', {
            type: 'ctaPopup'
        });
    };

    const toggleDislike = async () => {
        if (!canInteract) {
            openCta();
            return;
        }

        setButtonDisabled(true);
        try {
            if (!comment.disliked) {
                await dispatchAction('dislikeComment', comment);
                setDislikeAnimation('animate-heartbeat');
                setTimeout(() => {
                    setDislikeAnimation('');
                }, 400);
            } else {
                await dispatchAction('undislikeComment', comment);
            }
        } finally {
            setButtonDisabled(false);
        }
    };

    return (
        <button
            aria-label={comment.disliked ? t('Remove dislike') : t('Dislike')}
            className={`duration-50 group flex cursor-pointer items-center font-sans text-base outline-0 transition-all ease-linear sm:text-sm ${
                comment.disliked ? 'text-black/90 dark:text-white/90' : 'text-black/50 hover:text-black/75 dark:text-white/60 dark:hover:text-white/75'
            }`}
            data-testid="dislike-button"
            disabled={buttonDisabled}
            type="button"
            onClick={toggleDislike}
        >
            <ThumbsDownIcon
                className={dislikeAnimation + ` h-4 w-4 ${
                    comment.disliked ? 'fill-black dark:fill-white stroke-black dark:stroke-white' : 'stroke-black/50 group-hover:stroke-black/75 dark:stroke-white/60 dark:group-hover:stroke-white/75'
                } ${!comment.disliked && canInteract && 'group-hover:stroke-black/75 dark:group-hover:stroke-white/75'} transition duration-50 ease-linear`}
            />
        </button>
    );
};

export default LikeButton;
